import { Component, Input, OnInit } from '@angular/core';
import { TCenter } from '@shared/interfaces/TCenter';
import { TProject, TRegionBrick } from '@shared/interfaces/TProject';
import { computeMomentumLong, computeMomentumShort } from '@shared/utils/computeMomentum';
import { momentumLongDesc, momentumShortDesc } from 'src/app/constants/momentum-description.constant';
import { calculatePercent } from 'src/app/helpers/calculate-percent-helper';
import { Region } from 'src/app/interfaces/region.interface';
import { SalesInPack, SalesInPackCompetitor } from 'src/app/interfaces/sales-in-pack.interface';
import { YearMonth } from 'src/app/interfaces/year-month.interface';
import { KamReportsService } from '../../services/kam-reports.service';

@Component({
  selector: 'app-sales-in-pack',
  templateUrl: './sales-in-pack.component.html',
  styleUrls: ['./sales-in-pack.component.scss']
})
export class SalesInPackComponent implements OnInit {

  @Input() project: TProject;
  public salesInPack: SalesInPack;
  private closestDate: YearMonth;
  public momentumLongDesc: string = momentumLongDesc;
  public momentumShortDesc: string = momentumShortDesc;

  constructor(
    private _kamReportsService: KamReportsService,
  ) {}

  get centers(): TCenter[] { return this._kamReportsService.allCenters; }
  get selectedRegions(): Region[] { return this._kamReportsService.selectedRegions; }

  ngOnInit(): void {
    this.closestDate = this._kamReportsService.getClosestSalesDate(this.project.accountBricks);
    this.salesInPack = this.buildSalesInPack(this.project, this.selectedRegions);
  }

  /**
   * Build data for Sales in pack/vial table
   * @param  {TProject} project
   * @param  {TCenter[]} centers
   * @returns Observable
   */
  buildSalesInPack(project: TProject, selectedRegions: Region[]): SalesInPack {
    const filteredBricks: TRegionBrick[] = this._kamReportsService.filterBricksBySelectedRegions(project.accountBricks, selectedRegions);

    const salesNationalTotal = this._kamReportsService.getTotalFromClosestMonth(project.accountBricks, 'sales');
    const salesTerritoryTotal = this._kamReportsService.getTotalFromClosestMonth(filteredBricks, 'sales');
    const salesInEuroNationalTotal = this._kamReportsService.getTotalFromClosestMonth(project.accountBricks, 'salesInEuro');
    const salesInEuroTerritoryTotal = this._kamReportsService.getTotalFromClosestMonth(filteredBricks, 'salesInEuro');

    const salesNationalBudget = this._kamReportsService.sumAllPlannedBudgets(project.accountRegions, this.closestDate.year);
    const salesTerritoryBudget = this._kamReportsService.sumAllPlannedBudgets(project.accountRegions, this.closestDate.year, selectedRegions);
    const salesNationalPercent = calculatePercent(+salesNationalTotal, +salesNationalBudget);
    const salesTerritoryPercent = calculatePercent(+salesTerritoryTotal, +salesTerritoryBudget);
    const salesInEuroNationalBudget = this._kamReportsService.sumAllPlannedBudgets(project.accountRegions, this.closestDate.year);
    const salesInEuroTerritoryBudget = this._kamReportsService.sumAllPlannedBudgets(project.accountRegions, this.closestDate.year, selectedRegions);
    const salesInEuroNationalPercent = calculatePercent(+salesInEuroNationalTotal, +salesInEuroNationalBudget);
    const salesInEuroTerritoryPercent = calculatePercent(+salesInEuroTerritoryTotal, +salesInEuroTerritoryBudget);

    const salesNationalMomentumShort = Math.round(computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'sales')));
    const salesNationalMomentumLong = Math.round(computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'sales')));
    const salesTerritoryMomentumShort = Math.round(computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(filteredBricks, 16, 'sales')));
    const salesTerritoryMomentumLong = Math.round(computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(filteredBricks, 16, 'sales')));
    const salesInEuroNationalFourMonth = Math.round(computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'salesInEuro')));
    const salesInEuroNationalPy = Math.round(computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'salesInEuro')));
    const salesInEuroTerritoryFourMonth = Math.round(computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(filteredBricks, 16, 'salesInEuro')));
    const salesInEuroTerritoryPy = Math.round(computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(filteredBricks, 16, 'salesInEuro')));

    const competitors: SalesInPackCompetitor[] = [];
    if (!project.competitors) project.competitors = [];
    project.competitors.forEach(competitor => {
      const competitorSalesNationalTotal = this._kamReportsService.sumAllCompetitorData(competitor.id, project.accountBricks, 'sales');
      const competitorSalesTerritoryTotal = this._kamReportsService.sumAllCompetitorData(competitor.id, filteredBricks, 'sales');
      const competitorSalesNationalFourMonth = computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'sales'));
      const competitorSalesNationalPy = computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'sales'));
      const competitorSalesTerritoryFourMonth = computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(filteredBricks, 16, 'sales'));
      const competitorSalesTerritoryPy = computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(filteredBricks, 16, 'sales'));

      const competitorSalesInEuroNationalTotal = this._kamReportsService.sumAllCompetitorData(competitor.id, project.accountBricks, 'salesInEuro');
      const competitorSalesInEuroTerritoryTotal = this._kamReportsService.sumAllCompetitorData(competitor.id, filteredBricks, 'salesInEuro');
      const competitorSalesInEuroNationalFourMonth = computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'salesInEuro'));
      const competitorSalesInEuroNationalPy = computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(project.accountBricks, 16, 'salesInEuro'));
      const competitorSalesInEuroTerritoryFourMonth = computeMomentumShort(this._kamReportsService.getLastXMonthsTotal(filteredBricks, 16, 'salesInEuro'));
      const competitorSalesInEuroTerritoryPy = computeMomentumLong(this._kamReportsService.getLastXMonthsTotal(filteredBricks, 16, 'salesInEuro'));

      competitors.push({
        name: competitor.name,
        salesNationalTotal: isNaN(+competitorSalesNationalTotal) ? 'N/A' : Math.round(competitorSalesNationalTotal),
        salesTerritoryTotal: isNaN(+competitorSalesTerritoryTotal) ? 'N/A' : Math.round(competitorSalesTerritoryTotal),
        salesNationalFourMonth: isNaN(+competitorSalesNationalFourMonth) ? 'N/A' : Math.round(competitorSalesNationalFourMonth) + '%',
        salesTerritoryFourMonth: isNaN(+competitorSalesTerritoryFourMonth) ? 'N/A' : Math.round(competitorSalesTerritoryFourMonth) + '%',
        salesNationalPy: isNaN(+competitorSalesNationalPy) ? 'N/A' : Math.round(competitorSalesNationalPy) + '%',
        salesTerritoryPy: isNaN(+competitorSalesTerritoryPy) ? 'N/A' : Math.round(competitorSalesTerritoryPy) + '%',

        salesInEuroNationalTotal: isNaN(+competitorSalesInEuroNationalTotal) ? 'N/A' : Math.round(competitorSalesInEuroNationalTotal),
        salesInEuroTerritoryTotal: isNaN(+competitorSalesInEuroTerritoryTotal) ? 'N/A' : Math.round(competitorSalesInEuroTerritoryTotal),
        salesInEuroNationalFourMonth: isNaN(+competitorSalesInEuroNationalFourMonth) ? 'N/A' : Math.round(competitorSalesInEuroNationalFourMonth) + '%',
        salesInEuroTerritoryFourMonth: isNaN(+competitorSalesInEuroTerritoryFourMonth) ? 'N/A' : Math.round(competitorSalesInEuroTerritoryFourMonth) + '%',
        salesInEuroNationalPy: isNaN(+competitorSalesInEuroNationalPy) ? 'N/A' : Math.round(competitorSalesInEuroNationalPy) + '%',
        salesInEuroTerritoryPy: isNaN(+competitorSalesInEuroTerritoryPy) ? 'N/A' : Math.round(competitorSalesInEuroTerritoryPy) + '%',
      });
    });

    const result = {
      national: {
        salesTotal: isNaN(+salesNationalTotal) ? 'N/A' : Math.round(salesNationalTotal),
        salesFourMonth: isNaN(+salesNationalMomentumShort) ? 'N/A' : salesNationalMomentumShort + '%',
        salesPy: isNaN(+salesNationalMomentumLong) ? 'N/A' : salesNationalMomentumLong + '%',
        salesBudget: isNaN(+salesNationalBudget) ? 'N/A' : Math.round(salesNationalBudget),
        salesPercent: salesNationalPercent,

        salesInEuroTotal: isNaN(+salesInEuroNationalTotal) ? 'N/A' : new Intl.NumberFormat().format(Math.round(salesInEuroNationalTotal)),
        salesInEuroFourMonth: isNaN(+salesInEuroNationalFourMonth) ? 'N/A' : salesInEuroNationalFourMonth + '%',
        salesInEuroPy: isNaN(+salesInEuroNationalPy) ? 'N/A' : salesInEuroNationalPy + '%',
        salesInEuroBudget: isNaN(+salesInEuroNationalBudget) ? 'N/A' : Math.round(salesInEuroNationalBudget),
        salesInEuroPercent: salesInEuroNationalPercent,
      },
      territory: {
        salesTotal: isNaN(+salesTerritoryTotal) ? 'N/A' : Math.round(salesTerritoryTotal),
        salesFourMonth: isNaN(+salesTerritoryMomentumShort) ? 'N/A' : salesTerritoryMomentumShort + '%',
        salesPy: isNaN(+salesTerritoryMomentumLong) ? 'N/A' : salesTerritoryMomentumLong + '%',
        salesBudget: isNaN(+salesTerritoryBudget) ? 'N/A' : Math.round(salesTerritoryBudget),
        salesPercent: salesTerritoryPercent,

        salesInEuroTotal: isNaN(+salesInEuroTerritoryTotal) ? 'N/A' : new Intl.NumberFormat().format(Math.round(salesInEuroTerritoryTotal)),
        salesInEuroFourMonth: isNaN(+salesInEuroTerritoryFourMonth) ? 'N/A' : salesInEuroTerritoryFourMonth + '%',
        salesInEuroPy: isNaN(+salesInEuroTerritoryPy) ? 'N/A' : salesInEuroTerritoryPy + '%',
        salesInEuroBudget: isNaN(+salesInEuroTerritoryBudget) ? 'N/A' : Math.round(salesInEuroTerritoryBudget),
        salesInEuroPercent: salesInEuroTerritoryPercent,
      },
      competitors,
    }
    return result;
  }

}
